/**
 * Copilot Entity Store — publishes the currently-viewed detail entity so the
 * global GRC Copilot chat/advise calls can be grounded on it.
 *
 * Detail pages (risk / policy / obligation / …) call `useRegisterCopilotEntity`
 * to expose their loaded record (entityType + entityId + the record itself).
 * `MainLayout` reads `entity` and threads it into <GrcCopilotSidebar> as
 * `entityData`, so asking the chat "résume ce risque" is scoped to the entity
 * instead of failing with "je n'ai pas accès aux détails".
 */

import { useEffect } from 'react'
import { create } from 'zustand'

export interface CopilotEntityContext {
  entityType: string
  entityId: string
  /** The loaded record (fields the LLM can summarize/advise on). */
  data?: Record<string, unknown>
}

interface CopilotEntityState {
  entity: CopilotEntityContext | null
  setEntity: (entity: CopilotEntityContext | null) => void
  clearEntity: () => void
}

export const useCopilotEntityStore = create<CopilotEntityState>((set) => ({
  entity: null,
  setEntity: (entity) => set({ entity }),
  clearEntity: () => set({ entity: null }),
}))

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v)
  } catch {
    return ''
  }
}

/**
 * Detail-page helper: publishes the currently-viewed entity to the Copilot
 * store and clears it on unmount / navigation. Safe to call with undefined ids
 * (while the record is still loading) — it simply clears the context.
 */
export function useRegisterCopilotEntity(
  entityType: string | undefined | null,
  entityId: string | undefined | null,
  data?: Record<string, unknown> | null,
): void {
  const setEntity = useCopilotEntityStore((s) => s.setEntity)
  const clearEntity = useCopilotEntityStore((s) => s.clearEntity)
  // Re-run when the loaded record content changes (not just the id).
  const dataKey = data ? safeStringify(data) : ''

  useEffect(() => {
    if (entityType && entityId) {
      setEntity({ entityType, entityId, data: data ?? undefined })
    } else {
      clearEntity()
    }
    return () => clearEntity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, dataKey])
}
